import React, { useEffect, useState } from 'react'
import { useParams, generatePath, Link } from 'react-router-dom'
import api from 'helpers/api'
import { IOrganisationTeamDetail } from 'types'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import { useProject } from 'hooks/useProject'
import Button from 'components/button'
import { Table } from 'components/table'
import AssignPaidUserModal from 'views/team/assignPaidUserModal'
import Skeleton from 'components/skeleton'
import { People, Csv } from 'components/icons'
import { TEAM_DASHBOARD, PROFILE } from 'routes'
import { validateSourceUrl, exportToCsv } from 'helpers/utils'
import ProgressBar from 'components/progressBar'
import StatsCard from 'components/statsCard'
import InfoStrip from 'components/spreadsheet/components/infoStrip'
import Input from 'components/input'
import { formatShortDate, formatShortDateTime } from 'helpers/date'
import ViewSwitch from 'views/organisation/components/viewSwitch'
import { bucketByDay, cumulativeSeries } from 'views/organisation/helpers/chart'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
  ChartOptions
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import 'chartjs-adapter-luxon'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, TimeScale, Filler)

const TeamSeats: React.FC = () => {
  const params: { teamId?: string } = useParams()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { user, refreshUser } = useAuth()
  const { project } = useProject()

  const [loading, setLoading] = useState<boolean>(true)
  const [teamDetail, setTeamDetail] = useState<IOrganisationTeamDetail>()
  const [assignModalOpen, setAssignModalOpen] = useState<boolean>(false)
  const [userSearch, setUserSearch] = useState<string>('')
  const [view, setView] = useState<'chart' | 'table'>('table')

  const fetchTeam = () => {
    if (!params.teamId) return
    setLoading(true)
    api
      .getTeamSeats(params.teamId)
      .then((response) => setTeamDetail(response.data))
      .catch(displayErrorMessage)
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    document.title = 'Team Seat Management'
  }, [])

  useEffect(() => {
    fetchTeam()
  }, [params.teamId])

  const currentUserPublicId = user?.publicId

  const handleRevoke = (email: string, name: string) => {
    if (!params.teamId) return
    if (!window.confirm(`Are you sure you want to revoke ${name}'s paid seat?`)) return

    api
      .revokeTeamSeat(params.teamId, email)
      .then((response) => {
        setTeamDetail(response.data)
        setSnackbarMessage(`Revoked ${name}'s seat`)
        refreshUser()
      })
      .catch(displayErrorMessage)
  }

  const handleAssign = async (email: string) => {
    if (!params.teamId) return
    const response = await api.assignTeamSeat(params.teamId, email)
    setTeamDetail(response.data)
    const isPending = (response.data.pendingMembers ?? []).some((p) => p.email === email.toLowerCase())
    setSnackbarMessage(isPending ? `Invite created for ${email}` : `Assigned seat to ${email}`)
  }

  const computeFilteredMembers = () => {
    if (!teamDetail) return []
    if (!userSearch) return teamDetail.members
    const search = userSearch.toLowerCase()
    return teamDetail.members.filter(
      (m) => m.name.toLowerCase().includes(search) || m.email.toLowerCase().includes(search)
    )
  }
  const filteredMembers = computeFilteredMembers()

  const buildMembersTableData = () => {
    if (!teamDetail) return []
    const activeRows = filteredMembers.map((member) => {
      const isManager = teamDetail.managers.some((m) => m.publicId === member.publicId)
      const isExternal = member.isExternal ?? false
      const status = isManager ? 'Manager' : isExternal ? 'External' : 'Member'
      return {
        profilePicture: {
          value: member.email,
          label: (
            <img
              src={validateSourceUrl(member.profilePicture ?? undefined)}
              className="object-cover rounded-full mr-2.5 w-10 h-10"
              alt={member.name || 'Anonymous'}
            />
          )
        },
        name: { label: member.name, value: member.name },
        status: {
          label: (
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                isManager
                  ? 'bg-light-green text-green'
                  : isExternal
                    ? 'text-blue bg-really-light-blue'
                    : 'text-muted bg-surface-strong'
              }`}
            >
              {status}
            </span>
          ),
          value: status
        },
        email: { label: member.email, value: member.email },
        createdAt: {
          label: member.createdAt ? formatShortDate(member.createdAt) : '—',
          value: member.createdAt || ''
        },
        lastLogin: {
          label: member.lastLoginAt ? formatShortDateTime(member.lastLoginAt) : '—',
          value: member.lastLoginAt || ''
        },
        profile: {
          value: member.publicId,
          label: member.firebaseUserId ? (
            <Button
              to={generatePath(PROFILE, { userId: member.firebaseUserId, tab: 'information' })}
              internalType="outline"
            >
              View Profile
            </Button>
          ) : null
        },
        revoke: {
          label:
            member.publicId === currentUserPublicId ? (
              <span className="text-xs text-muted italic">You</span>
            ) : (
              <Button internalType="danger" onClick={() => handleRevoke(member.email, member.name)}>
                Revoke Seat
              </Button>
            ),
          value: member.publicId === currentUserPublicId ? 'self' : 'revoke'
        }
      }
    })
    const pendingRows = (teamDetail.pendingMembers ?? []).map((p) => ({
      profilePicture: { value: p.email, label: <div className="w-10 h-10 rounded-full bg-surface-strong mr-2.5" /> },
      name: { label: p.email, value: p.email },
      status: {
        label: (
          <span className="text-xs text-orange bg-really-light-yellow px-2 py-0.5 rounded-full font-bold">Invited</span>
        ),
        value: 'Invited'
      },
      email: { label: p.email, value: p.email },
      createdAt: { label: '—', value: '' },
      lastLogin: { label: '—', value: '' },
      profile: { value: '', label: null },
      revoke: {
        label: (
          <Button internalType="danger" onClick={() => handleRevoke(p.email, p.email)}>
            Revoke Invite
          </Button>
        ),
        value: 'pending'
      }
    }))
    return [...activeRows, ...pendingRows]
  }

  const membersTableData = buildMembersTableData()

  const computeMemberDaily = () => {
    if (!teamDetail) return []
    return bucketByDay(teamDetail.members, (m) => m.createdAt)
  }
  const memberDaily = computeMemberDaily()

  const memberSeries = cumulativeSeries(memberDaily)

  const memberGrowthChart = {
    datasets: [
      {
        label: 'Cumulative paid users',
        data: memberSeries,
        fill: true,
        borderColor: project.primaryColour,
        backgroundColor: `${project.primaryColour}1A`,
        cubicInterpolationMode: 'monotone' as const
      }
    ]
  }

  const timeScaleOpts: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    parsing: false,
    scales: {
      // Let the time scale auto-pick its unit (day/week/month/year) from the
      // data range and cap the tick count, otherwise a multi-month/year range
      // crams unreadable day-level labels together.
      x: {
        type: 'time',
        bounds: 'data',
        ticks: { autoSkip: true, maxTicksLimit: 8, maxRotation: 0 },
        grid: { display: false }
      },
      y: { ticks: { precision: 0 }, beginAtZero: true }
    },
    // `datalabels` is disabled explicitly: the spreadsheet chart registers
    // chartjs-plugin-datalabels *globally*, so without this every one of the
    // ~one-point-per-day series values gets stamped onto the plot, smearing
    // the chart into an unreadable mess.
    plugins: { legend: { display: false }, datalabels: { display: false } },
    elements: {
      point: { radius: 0, hitRadius: 6, hoverRadius: 3 }
    }
  }

  const handleExport = () => {
    if (!teamDetail) return
    const headers = ['Name', 'Email', 'Signed Up', 'Last Login', 'Role']
    const rows = filteredMembers.map((m) => [
      m.name || '',
      m.email || '',
      m.createdAt ? new Date(m.createdAt).toLocaleDateString() : '',
      m.lastLoginAt ? new Date(m.lastLoginAt).toLocaleString() : 'Never',
      teamDetail.managers.some((mgr) => mgr.publicId === m.publicId) ? 'Manager' : 'Member'
    ])
    exportToCsv(`${teamDetail.name} - Paid Users.csv`, headers, rows)
  }

  const searchInputStyle = {
    maxWidth: '200px',
    width: '200px',
    height: '40px',
    padding: '0 10px',
    boxSizing: 'border-box' as const
  }

  return (
    <div className="w-full p-5 flex flex-col gap-4 h-screen min-h-0">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to={generatePath(TEAM_DASHBOARD, { teamId: params.teamId || '' })}
          className="text-muted hover:text-body transition-all text-sm"
        >
          &larr; Back to Dashboard
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <h1 className="mr-auto flex items-center gap-2">
          <People className="h-5 w-5" />
          {teamDetail?.name ?? 'Team'} — Seat Management
        </h1>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="grid grid-cols-4 max-lg:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[80px]" />
          ))}
        </div>
      ) : (
        teamDetail && (
          <>
            <div className="grid grid-cols-4 max-lg:grid-cols-2 gap-4">
              <StatsCard label="Seat Limit" value={teamDetail.seatLimit} />
              <StatsCard label="Seats Used" value={teamDetail.seatsUsed} />
              <StatsCard label="Seats Available" value={teamDetail.seatsAvailable} />
              <StatsCard label="Managers">
                <div className="mt-1 font-bold">{teamDetail.managers.map((m) => m.name).join(', ')}</div>
              </StatsCard>
            </div>

            <ProgressBar
              percentage={teamDetail.seatLimit > 0 ? (teamDetail.seatsUsed / teamDetail.seatLimit) * 100 : 0}
              autoColour
            />

            <InfoStrip
              text={`You can assign or revoke paid user seats for users in this team. You have ${teamDetail.seatsAvailable} seat${teamDetail.seatsAvailable !== 1 ? 's' : ''} remaining. Contact an Org Admin to increase your seat limit.`}
            />
          </>
        )
      )}

      {/* Paid Users table/chart */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Paid Users</h2>
          <div className="flex items-center gap-1">
            {view === 'table' && (
              <Input
                placeholder="Search for a user..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                style={searchInputStyle}
              />
            )}
            <ViewSwitch value={view} onChange={setView} />
            <Button className="mr-1 h-10" onClick={handleExport} internalType="outline" title="Export to CSV">
              <Csv className="mr-1" />
              Export
            </Button>
            <Button
              internalType="accept"
              onClick={() => setAssignModalOpen(true)}
              disabled={teamDetail ? teamDetail.seatsAvailable <= 0 : true}
            >
              + Assign Paid User
            </Button>
          </div>
        </div>
        {view === 'chart' ? (
          <Line data={memberGrowthChart} options={timeScaleOpts} />
        ) : (
          <Table
            include={[
              { header: '', id: 'profilePicture' },
              { header: 'Name', id: 'name' },
              { header: 'Status', id: 'status' },
              { header: 'Email', id: 'email' },
              { header: 'Signed Up', id: 'createdAt' },
              { header: 'Last Signed In', id: 'lastLogin' },
              { header: 'Profile', id: 'profile' },
              { header: '', id: 'revoke' }
            ]}
            data={membersTableData}
            loading={loading}
            defaultSort="name"
            defaultSortAscending={true}
          />
        )}
      </div>

      {assignModalOpen && (
        <AssignPaidUserModal open={assignModalOpen} onClose={() => setAssignModalOpen(false)} onAssign={handleAssign} />
      )}
    </div>
  )
}

export default TeamSeats
